import InputField from 'components/fields/InputField';
import React, { useEffect, useState } from 'react';
import { RiEyeCloseLine, RiEyeLine } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import departmentService from 'ApiService/SuperAdmin/departmentService';

const TeacherInfo = ({ setTeacherData, teacherData, setDisable }) => {
  const [errors, setErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [loadingDepts, setLoadingDepts] = useState(false);

  const capitalizeWords = (str) => {
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const isValidPhone = (phone) => /^[6-9]\d{9}$/.test(phone); // Indian phone validation

  const validateField = (field, value) => {
    const errors = {};

    switch (field) {
      case 'teacherName':
        if (!value.trim()) {
          errors.teacherName = 'Teacher name is required.';
        }
        break;
      case 'teacherEmail':
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(value)) {
          errors.teacherEmail = 'Please enter a valid email address.';
        }
        break;
      case 'contactNumber':
        if (!isValidPhone(value)) {
          errors.contactNumber =
            'Please enter a valid phone number (10 digits, starting with 6-9).';
        }
        break;
      case 'teacherPassword':
        const passwordPattern =
          /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,15}$/;
        if (!passwordPattern.test(value)) {
          errors.teacherPassword =
            'Password must be 8-15 characters long, contain at least one uppercase letter, one number, and one special character (!@#$%^&*).';
        }
        break;
      default:
        break;
    }

    setFieldErrors((prevErrors) => ({
      ...prevErrors,
      ...errors,
      [field]: errors[field] || '',
    }));
  };

  const handleInputChange = (event) => {
    const { id, value } = event.target;

    const formattedValue =
      id === 'teacherName' ? capitalizeWords(value) : value;

    setTeacherData((prevData) => ({
      ...prevData,
      [id]: formattedValue,
    }));

    validateField(id, formattedValue);
  };

  useEffect(() => {
    const fetchDepartments = async () => {
      setLoadingDepts(true);
      try {
        const response = await departmentService.getAllDepartments();
        if (response.data?.success) {
          setDepartments(response.data.data || []);
        }
      } catch (error) {
        console.error('Error fetching departments:', error);
      } finally {
        setLoadingDepts(false);
      }
    };
    fetchDepartments();
  }, []);

  useEffect(() => {
    const newErrors = [];
    const {
      teacherName,
      teacherEmail,
      teacherPassword,
      contactNumber,
      department,
    } = teacherData;

    if (
      !teacherName ||
      !teacherEmail ||
      !teacherPassword ||
      !contactNumber.trim() ||
      !department
    ) {
      newErrors.push('Please fill in all fields.');
    }

    if (Object.values(fieldErrors).some((error) => error)) {
      newErrors.push('Please correct the highlighted errors.');
    }

    setErrors(newErrors);
    setDisable(newErrors.length > 0);
  }, [teacherData, fieldErrors, setDisable]);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <div className="h-full w-full rounded-[20px] px-3 pt-7 md:px-8">
      <h4 className="pt-[5px] text-xl font-bold text-navy-700 dark:text-white">
        Teacher Information
      </h4>

      <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
        {/* Teacher Name */}
        <div>
          <InputField
            extra="mb-3"
            label="Teacher Name"
            placeholder="Enter Teacher Name"
            id="teacherName"
            type="text"
            onChange={handleInputChange}
            value={teacherData?.teacherName}
            maxLength={40}
          />
          {fieldErrors.teacherName && (
            <div className="text-sm text-red-500">
              {fieldErrors.teacherName}
            </div>
          )}
        </div>

        {/* Teacher Email */}
        <div>
          <InputField
            extra="mb-3"
            label="Teacher Email"
            placeholder="Enter Teacher Email"
            id="teacherEmail"
            type="email"
            onChange={handleInputChange}
            value={teacherData?.teacherEmail}
            maxLength={40}
          />
          {fieldErrors.teacherEmail && (
            <div className="text-sm text-red-500">
              {fieldErrors.teacherEmail}
            </div>
          )}
        </div>

        {/* Contact Number */}
        <div>
          <InputField
            extra="mb-3"
            label="Contact Number"
            placeholder="Enter Contact Number"
            id="contactNumber"
            type="text"
            onChange={handleInputChange}
            value={teacherData?.contactNumber}
            maxLength={10}
          />
          {fieldErrors.contactNumber && (
            <div className="text-sm text-red-500">
              {fieldErrors.contactNumber}
            </div>
          )}
        </div>

        {/* Teacher Password */}
        <div className="relative mb-3 h-[calc(2.5rem+1rem)]">
          <div>
            <InputField
              extra="mb-3"
              label="Teacher Password"
              placeholder="Enter Teacher Password"
              id="teacherPassword"
              type={showPassword ? 'text' : 'password'}
              onChange={handleInputChange}
              value={teacherData?.teacherPassword}
              maxLength={15}
            />
            <button
              type="button"
              className="absolute right-4 top-12 -translate-y-1/2 transform pt-4 text-lg text-navy-700 dark:text-white "
              onClick={togglePasswordVisibility}
            >
              {showPassword ? <RiEyeLine /> : <RiEyeCloseLine />}
            </button>
          </div>
          {fieldErrors.teacherPassword && (
            <div className="text-sm text-red-500">
              {fieldErrors.teacherPassword}
            </div>
          )}
        </div>

        {/* Department */}
        <div className="mb-3">
          <label className="mb-2 block text-sm font-bold text-navy-700 dark:text-white">
            Department
          </label>
          <select
            id="department"
            onChange={handleInputChange}
            value={teacherData?.department || ''}
            className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-navy-700 dark:bg-navy-900 dark:text-white"
          >
            <option value="">Select Department</option>
            {departments.map((dept) => (
              <option key={dept._id} value={dept._id}>
                {dept.name}
              </option>
            ))}
          </select>
          {fieldErrors.department && (
            <div className="text-sm text-red-500">{fieldErrors.department}</div>
          )}
        </div>
      </div>

      {/* Global Errors */}
      {errors.length > 0 && (
        <div className="mt-4 text-red-500">
          {errors.map((error, index) => (
            <p key={index}>{error}</p>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeacherInfo;
